/**
 * What happens after a session begins at the house, whichever way it began.
 *
 * Password login and Google sign-in end in the same place: fetch what the
 * person holds, then let `afterLoginDestination` pick between `?next=`, the
 * remembered shell and the fallback. This used to live inline in Login.tsx;
 * it is here so the Google done-page runs the SAME decision rather than a
 * near-copy that drifts.
 *
 * Pure: the billing call and the window facts come in as arguments, so the
 * test can exercise the held/not-held branches without a DOM.
 */
import { afterLoginDestination, type Destination } from './houseLogin';
import type { BillingInfo } from '../types';

export async function settleLogin(args: {
  getBilling: () => Promise<BillingInfo>;
  next: string | null;
  lastShell: string | null;
  hostname: string;
  origin: string;
  fallback: string;
}): Promise<Destination> {
  // Entitlements are fetched here rather than through the context because
  // that provider mounts inside ProtectedRoute, after the login page. A failed
  // fetch reads as "holds nothing", which lands in the house.
  const billing = await args.getBilling().catch(() => null);
  const held = (billing?.products ?? []).filter((e) => e.active).map((e) => e.product);
  return afterLoginDestination({
    next: args.next,
    lastShell: args.lastShell,
    held,
    hostname: args.hostname,
    origin: args.origin,
    fallback: args.fallback,
  });
}

/**
 * Go there. Returns true when the browser is leaving the SPA (a full load),
 * so the caller can keep its "Signing in..." state up until the new page
 * paints instead of flashing the form back.
 */
export function follow(
  dest: Destination,
  navigate: (to: string, opts: { replace: boolean }) => void,
  replaceHref: (href: string) => void = (href) => window.location.replace(href),
): boolean {
  if (dest.kind === 'href') {
    replaceHref(dest.href);
    return true;
  }
  navigate(dest.to, { replace: true });
  return false;
}
